'use client';

import { useState } from 'react';
import {
  CheckCircle2,
  Clock3,
  CreditCard,
  LifeBuoy,
  Mail,
  MapPin,
  Megaphone,
  Send,
  Settings2,
} from 'lucide-react';
import { PageShell } from '@/components/design/PageShell';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';
import { useI18n } from '@/contexts/I18nContext';

export default function SupportPage() {
  const { t } = useI18n();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      setSubmitStatus('error');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');
    try {
      const response = await fetch('/api/support/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      if (!response.ok) throw new Error('Failed to send message');
      setSubmitStatus('success');
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      console.error('Contact form error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const setField = (key: keyof typeof formData, value: string) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const routes = [
    { icon: Settings2, title: t('support.technical'), text: t('support.routeTechnical') },
    { icon: CreditCard, title: t('support.billing'), text: t('support.routeBilling') },
    { icon: Megaphone, title: t('support.campaign'), text: t('support.routeCampaign') },
  ];

  const priorityItems = [
    t('support.priorityOne'),
    t('support.priorityTwo'),
    t('support.priorityThree'),
  ];

  return (
    <PageShell>
      <section className="relative pt-4 sm:pt-36">
        <div className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-[620px] bg-brand-mesh opacity-80" />
        <div className="section-shell">
          <div className="grid gap-8 lg:grid-cols-[1fr_0.82fr] lg:items-end">
            <div>
              <div className="section-kicker">{t('support.eyebrow')}</div>
              <h1 className="mt-6 max-w-4xl text-balance text-5xl font-semibold leading-[0.98] tracking-normal text-ink-950 sm:text-6xl lg:text-7xl">
                {t('support.title')}
              </h1>
              <p className="body-copy mt-6 max-w-2xl">
                {t('support.description')}
              </p>
            </div>

            <div className="soft-card p-5 sm:p-6">
              <div className="flex items-center gap-3">
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-500 text-white shadow-glow">
                  <Clock3 className="h-5 w-5" />
                </span>
                <div>
                  <p className="text-sm font-medium text-ink-500">{t('common.responseTime')}</p>
                  <p className="text-2xl font-semibold text-ink-950">{t('common.within24h')}</p>
                </div>
              </div>
              <div className="mt-6 grid gap-3 text-sm text-ink-600">
                <a href="mailto:info@cannect.ai" className="flex items-center gap-2 rounded-2xl bg-ink-50 px-4 py-3 transition hover:bg-brand-50 hover:text-brand-700">
                  <Mail className="h-4 w-4" />
                  info@cannect.ai
                </a>
                <div className="flex items-start gap-2 rounded-2xl bg-ink-50 px-4 py-3">
                  <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-brand-600" />
                  <div className="leading-6">
                    <p>{t('common.companyAddressLine1')}</p>
                    <p>{t('common.companyAddressLine2')}</p>
                    <p>{t('common.companyAddressLine3')}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section-shell py-14">
        <div className="grid gap-4 md:grid-cols-3">
          {routes.map((item) => {
            const Icon = item.icon;
            return (
              <article key={item.title} className="soft-card p-6">
                <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-50 text-brand-600">
                  <Icon className="h-5 w-5" />
                </div>
                <h2 className="text-xl font-semibold text-ink-950">{item.title}</h2>
                <p className="mt-3 text-sm leading-6 text-ink-600">{item.text}</p>
              </article>
            );
          })}
        </div>

        <div className="mt-8 grid gap-6 lg:grid-cols-[0.72fr_1fr]">
          <aside className="soft-card p-6 sm:p-8">
            <div className="mb-6 flex h-12 w-12 items-center justify-center rounded-2xl bg-ink-950 text-white">
              <LifeBuoy className="h-5 w-5" />
            </div>
            <h2 className="text-3xl font-semibold tracking-normal text-ink-950">
              {t('support.routesTitle')}
            </h2>
            <p className="mt-3 text-sm leading-6 text-ink-600">
              {t('support.tellUs')}
            </p>

            <div className="mt-8 rounded-[1.5rem] border border-ink-950/10 bg-ink-50 p-5">
              <h3 className="text-sm font-semibold uppercase tracking-[0.16em] text-ink-500">
                {t('support.priorityTitle')}
              </h3>
              <ul className="mt-4 space-y-3">
                {priorityItems.map((item) => (
                  <li key={item} className="flex gap-3 text-sm leading-6 text-ink-700">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-brand-600" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </aside>

          <div className="soft-card p-6 sm:p-8">
            <div className="mb-7 flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-500 text-white shadow-glow">
                <Send className="h-5 w-5" />
              </div>
              <div>
                <h2 className="text-2xl font-semibold text-ink-950">{t('support.sendMessage')}</h2>
                <p className="text-sm text-ink-500">{t('support.tellUs')}</p>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid gap-5 sm:grid-cols-2">
                <Input label={t('support.yourName')} value={formData.name} onChange={(e) => setField('name', e.target.value)} required />
                <Input label={t('common.email')} type="email" value={formData.email} onChange={(e) => setField('email', e.target.value)} required />
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium">{t('support.subject')}</label>
                <Select
                  value={formData.subject}
                  onChange={(value) => setField('subject', value)}
                  options={[
                    { value: 'general', label: t('support.general') },
                    { value: 'technical', label: t('support.technical') },
                    { value: 'billing', label: t('support.billing') },
                    { value: 'campaign', label: t('support.campaign') },
                    { value: 'other', label: t('support.other') },
                  ]}
                  placeholder={t('common.selectTopic')}
                />
              </div>
              <Textarea
                placeholder={t('support.describe')}
                value={formData.message}
                onChange={(e) => setField('message', e.target.value)}
                required
                className="min-h-[170px]"
              />

              {submitStatus === 'success' && (
                <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-700">
                  {t('support.success')}
                </div>
              )}
              {submitStatus === 'error' && (
                <div className="rounded-2xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                  {t('support.error')}
                </div>
              )}

              <Button type="submit" variant="brand" size="lg" className="w-full" disabled={isSubmitting}>
                {isSubmitting ? t('common.sending') : t('support.sendMessage')}
              </Button>
            </form>
          </div>
        </div>
      </section>
    </PageShell>
  );
}
